import React, { ReactNode, useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import "./index.less";

type MessageOptions = {
  duration?: number;
  className?: string;
  onUnMount?: () => void;
};

type MessageFCProps = {
  content: ReactNode;
} & MessageOptions;

type MessageFunc = {
  destroy: () => void;
};

const MessageFC: React.FC<MessageFCProps> = ({ content, duration = 3, onUnMount }) => {
  const [visible, setVisible] = useState(true);

  const close = () => {
    setVisible(false);
    onUnMount?.();
  };

  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        close();
      }, duration * 1000);
      return () => clearTimeout(timer);
    }
  }, [duration]);

  if (!visible) return null;

  return content;
};

const createMessage = (content: ReactNode, type: "info" | "loading", options?: MessageOptions): MessageFunc => {
  let wrapper = document.querySelector(".apex-message-wrapper");
  let mask = document.querySelector(".apex-message-mask");
  if (!mask) {
    mask = document.createElement("div");
    mask.classList.add("apex-message-mask");
    document.body.appendChild(mask);
  }
  if (!wrapper) {
    wrapper = document.createElement("div");
    wrapper.classList.add("apex-message-wrapper");
    document.body.appendChild(wrapper);
  }
  const container = document.createElement("div");
  container.classList.add("apex-message", type);
  wrapper.appendChild(container);
  const root = createRoot(container);

  const destroy = () => {
    root.unmount();
    wrapper.removeChild(container);
    if (wrapper.childNodes.length == 0) {
      document.body.removeChild(mask);
      document.body.removeChild(wrapper);
    }
  };

  root.render(<MessageFC content={content} onUnMount={destroy} {...options} />);

  return { destroy };
};

const Message = {
  info(content: ReactNode, options?: MessageOptions) {
    return createMessage(content, "info", options);
  },
  loading(content?: ReactNode, options?: MessageOptions) {
    let hide = false;
    let func: MessageFunc;
    const timer = setTimeout(() => {
      if (!hide) {
        options = options ?? {};
        options.duration = options?.duration ?? 0;
        func = createMessage(content, "loading", options);
      }
    }, 700);

    return {
      destroy: () => {
        hide = true;
        clearTimeout(timer);
        if (func) {
          func.destroy();
        }
      },
    };
  },
};

export default Message;
